import apiClient from "./axiosConfig";

// Test API connectivity
export const testFinanceAPI = async () => {
  try {
    // Test API by making a simple request
    await apiClient.get('/finance/journals/count/');
    return true;
  } catch (error) {
    return false;
  }
};

// Get journals list
export const getJournalsList = async (params = {}) => {
  try {
    // Test API first
    const isAPIWorking = await testFinanceAPI();
    if (!isAPIWorking) {
      throw new Error('Finance API is not accessible');
    }
    
    const queryParams = new URLSearchParams();

    // Accept multiple alias keys for customer id to be resilient
    const customerId = (params && (params.customer ?? params.customer_id ?? params.customerId));
    if (customerId) queryParams.append('customer_id', customerId);
    
    // Add search parameter
    if (params.search) queryParams.append('search', params.search);
    
    // Add date range parameters (from/to instead of start_date/end_date)
    if (params.from || params.date_from) queryParams.append('from', params.from || params.date_from);
    if (params.to || params.date_to) queryParams.append('to', params.to || params.date_to);
    
    // Add transaction type filter (debit/credit/sales)
    if (params.type || params.tnx_type) {
      const typeValue = params.type || params.tnx_type;
      queryParams.append('type', typeValue);
      // Adding type parameter
    }
    
    // Add customer filter
    if (params.customer) queryParams.append('customer', params.customer);
    
    // Add page_from parameter (always include 'j' for JournalView)
    queryParams.append('page_from', 'j');
    
    const url = `/finance/journals/?${queryParams.toString()}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Finance Journal API - Error fetching journals:', error);
    throw error;
  }
};

// Get journals list for CustomerDetails (without test API call)
export const getCustomerJournalsList = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Accept multiple alias keys for customer id to be resilient
    const customerId = (params && (params.customer ?? params.customer_id ?? params.customerId));
    if (customerId) queryParams.append('customer_id', customerId);
    if (params.search) queryParams.append('search', params.search);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    // Add page_from parameter (always include 'j' for JournalView)
    queryParams.append('page_from', 'j');
    
    const url = `/finance/journals/?${queryParams.toString()}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Finance Journal API - Error fetching customer journals:', error);
    throw error;
  }
};

// Get journal detail
export const getJournalDetail = async (journalId) => {
  try {
    const response = await apiClient.get(`/finance/journals/${journalId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching journal detail:', error);
    throw error;
  }
};

// List cashbox journal entries (Financial Overview)
// type: 'all' | 'deposit' | 'credit' | 'withdraw' | 'debit'
export const getCashboxJournals = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    const { type, from, to, date_from, date_to, search } = params;

    if (type && type !== "all") {
      const typeParam =
        type === "credit" ? "deposit" : type === "debit" ? "withdraw" : type;
      queryParams.append("type", typeParam);
    }

    const fromDate = from || date_from;
    const toDate = to || date_to;
    if (fromDate) queryParams.append("from", fromDate);
    if (toDate) queryParams.append("to", toDate);

    if (search && String(search).trim().length > 0) {
      queryParams.append("search", String(search).trim());
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/finance/journals/cashbox/?${queryString}`
      : "/finance/journals/cashbox/";

    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Finance Journal API - Error fetching cashbox journals:", error);
    throw error;
  }
};

// Update cashbox journal entry (Profit Management — PATCH)
export const patchCashboxJournal = async (journalId, { amount, note, tnx_date }) => {
  try {
    const payload = {};
    if (amount !== undefined && amount !== null) {
      payload.amount =
        typeof amount === "number" ? amount.toFixed(2) : String(amount);
    }
    if (note !== undefined) {
      payload.note = note || "";
    }
    if (tnx_date) {
      payload.tnx_date = tnx_date;
    }

    const response = await apiClient.patch(
      `/finance/journals/cashbox/${journalId}/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Finance Journal API - Error updating cashbox journal:", error);
    throw error;
  }
};

// Delete cashbox journal entry (Profit Management — DELETE)
export const deleteCashboxJournal = async (journalId) => {
  try {
    const response = await apiClient.delete(
      `/finance/journals/cashbox/${journalId}/`
    );
    return response.data;
  } catch (error) {
    console.error("Finance Journal API - Error deleting cashbox journal:", error);
    throw error;
  }
};

// Create cashbox journal entry (Financial Overview — deposit / withdraw)
export const createCashboxJournal = async ({ tnx_type, amount, tnx_date, note }) => {
  try {
    const payload = {
      is_cashbox_entry: true,
      tnx_type,
      amount:
        typeof amount === "number"
          ? amount.toFixed(2)
          : String(amount),
      tnx_date,
      note: note || "",
    };

    const response = await apiClient.post("/finance/journals/cashbox/", payload);
    return response.data;
  } catch (error) {
    console.error("Finance Journal API - Error creating cashbox journal:", error);
    throw error;
  }
};

// Create new journal
export const createJournal = async (journalData) => {
  try {
    const payload = {
      title: journalData.title,
      tnx_id: journalData.tnx_id,
      tnx_date: journalData.tnx_date,
      customer: journalData.customer,
      ...(journalData.customer_name ? { customer_name: journalData.customer_name } : {}),
      amount: parseFloat(journalData.amount),
      tnx_type: journalData.tnx_type,
      note: journalData.note || ""
    };
    
    const response = await apiClient.post('/finance/journals/', payload);
    return response.data;
  } catch (error) {
    console.error('Finance Journal API - Error creating journal:', error);
    throw error;
  }
};

// Update journal (PUT - full update)
export const updateJournal = async (journalId, journalData) => {
  try {
    const payload = {
      title: journalData.title,
      tnx_id: journalData.tnx_id,
      tnx_date: journalData.tnx_date,
      customer: journalData.customer,
      amount: parseFloat(journalData.amount),
      tnx_type: journalData.tnx_type,
      note: journalData.note || ""
    };
    
    const response = await apiClient.put(`/finance/journals/${journalId}/`, payload);
    return response.data;
  } catch (error) {
    console.error('Finance Journal API - Error updating journal:', error);
    throw error;
  }
};

// Partial update journal (PATCH)
export const partialUpdateJournal = async (journalId, journalData) => {
  try {
    const response = await apiClient.patch(`/finance/journals/${journalId}/`, journalData);
    return response.data;
  } catch (error) {
    console.error('Error partially updating journal:', error);
    throw error;
  }
};

// Delete journal
export const deleteJournal = async (journalId) => {
  try {
    const response = await apiClient.delete(`/finance/journals/${journalId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting journal:', error);
    throw error;
  }
};

// Get journal metrics (daily report — page_from=d)
export const getJournalMetrics = async (date = null) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('page_from', 'd');
    if (date) queryParams.append('date', date);

    const response = await apiClient.get(
      `/finance/journals/count/?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching journal metrics:', error);
    throw error;
  }
};

// Get finance report metrics
export const getFinanceReportMetrics = async () => {
  try {
    const response = await apiClient.get('/finance/journals/finance_report/');
    return response.data;
  } catch (error) {
    console.error('Finance Journal API - Error fetching finance report:', error);
    throw error;
  }
};

// Get journals list for DailyReportView (without page_from parameter)
export const getDailyReportJournals = async (params = {}, abortSignal = null) => {
  try {
    const queryParams = new URLSearchParams();

    // Accept multiple alias keys for customer id to be resilient
    const customerId = (params && (params.customer ?? params.customer_id ?? params.customerId));
    if (customerId) queryParams.append('customer_id', customerId);
    
    // Add search parameter - ensure complete search text is sent
    if (params.search && params.search.trim().length > 0) {
      queryParams.append('search', params.search.trim());
    }
    
    // Add date range parameters
    if (params.from || params.date_from) queryParams.append('from', params.from || params.date_from);
    if (params.to || params.date_to) queryParams.append('to', params.to || params.date_to);
    
    // Add transaction type filter (debit/credit/sales)
    if (params.type || params.tnx_type) {
      const typeValue = params.type || params.tnx_type;
      queryParams.append('type', typeValue);
    }
    
    // Add customer filter
    if (params.customer) queryParams.append('customer', params.customer);

    // Daily report uses page_from=d
    queryParams.append('page_from', 'd');

    const url = `/finance/journals/?${queryParams.toString()}`;
    
    // Add abort signal to request config if provided
    const config = abortSignal ? { signal: abortSignal } : {};
    
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    // Don't log cancelled requests as errors
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      throw error;
    }
    console.error('Finance Journal API - Error fetching daily report journals:', error);
    throw error;
  }
};